import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { css } from '@emotion/css';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Tab, Tabs } from '@material-ui/core';

import { useUnsafeTranslation } from '../../i18next';
import { useQueryParam } from '../../routes';
import { useProductAnalytics } from '../../services/productAnalytics';

type SelectedTabs = 'Automation' | 'Task' | 'Message' | 'People' | 'Notes';

const Container = styled('div')<{
  active: boolean;
}>`
  display: ${(p) => (p.active ? 'flex' : 'none')};
  height: 100%;
  width: 100%;
  min-width: 0;
`;

const TAB_NAMES = ['item', 'people', 'notes'] as const;
export type TimelineDialogTabName = typeof TAB_NAMES[number];

export const useTimelineDialogTabQueryParam = (
  param: string = 'tab',
  defaultTab: TimelineDialogTabName = 'item'
) => {
  return useQueryParam<TimelineDialogTabName>(
    param,
    (v) =>
      v && TAB_NAMES.includes(v as TimelineDialogTabName)
        ? (v as TimelineDialogTabName)
        : defaultTab,
    (v) => (v ? v : undefined)
  );
};

export const TabPanel: React.FC<{
  active: boolean;
}> = ({ active, children }) => {
  return <Container active={active}>{children}</Container>;
};

export const TimelineDialogTabs: React.FC<{
  label: string;
  previewTab: TimelineDialogTabName;
  onChange: (v: TimelineDialogTabName) => void;
  elementId: string;
  title: string;
  experienceTemplateId: string;
  type: 'AUTOMATION' | 'TASK' | 'MESSAGE';
  selectedTab: SelectedTabs;
}> = ({
  label,
  onChange,
  previewTab,
  experienceTemplateId,
  elementId,
  type,
  title,
  selectedTab
}) => {
  const { t } = useUnsafeTranslation();
  const { trackEvent } = useProductAnalytics();
  const th = useTheme();
  const root = css({
    '&&': {
      paddingTop: th.spacing(2),
      paddingBottom: th.spacing(2),
      '&:first-of-type': {
        paddingLeft: th.spacing(0)
      }
    }
  });
  const trackTabChange = (tab: SelectedTabs) => {
    trackEvent({
      eventType: PAEventType.EXPERIENCE_TPL,
      name: 'Timeline Item Modal Tab Changed',
      'Experience Template ID': experienceTemplateId,
      Explanation:
        'Admin user switches between People/Item tabs on the view modal for a task/message/automation',
      'Timeline Item ID': elementId,
      'Timeline Item Type': type,
      'Timeline Item Title': title,
      'Tab Selected': tab
    });
  };
  return (
    <Tabs
      value={previewTab}
      onChange={(ev, newValue: TimelineDialogTabName) => onChange(newValue)}
      indicatorColor="primary"
      textColor="primary"
      style={{ borderWidth: 0, borderColor: 'white' }}
    >
      <Tab
        value="item"
        label={t(label)}
        classes={{ root }}
        onClick={() => trackTabChange(selectedTab)}
      />
      <Tab
        value="people"
        label={'People'}
        classes={{ root }}
        onClick={() => trackTabChange('People')}
      />
      <Tab
        value="notes"
        label={t('Notes')}
        classes={{ root }}
        onClick={() => trackTabChange('Notes')}
      />
    </Tabs>
  );
};
